import React, { useState } from "react"
import { View, StyleSheet, TouchableOpacity } from "react-native"
import { connect } from "react-redux"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import * as amplitude from "@amplitude/analytics-react-native"

import TextM from "@common/components/text/TextM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { ModalM } from "@common/components/modal/ModalM"
import { theme } from "@common/theme/theme"
import { apiRequest, apiIdle } from "@data/redux/actions/api"
import { activitiesService } from "@data/services/activitiesService"
import { useApiStatus } from "@common/hooks/useApiStatus"
import {
    QUIZ_SIGNAL_TYPES,
    QUIZ_SIGNAL_LABELS,
    QUIZ_SIGNAL_ICONS,
} from "../quizSignalConstants"
import UncontrolledTextInputM from "@common/components/input/UncontrolledTextInputM"

const QuizQuestionSignalButton = (props) => {
    const {
        activityId,
        questionId,
        context = "during_quiz",
        onSignalSubmitted,
        buttonStyle,
        label = "Un souci avec cette question ?",
    } = props

    const [showModal, setShowModal] = useState(false)
    const [selectedType, setSelectedType] = useState(null)
    const [comment, setComment] = useState("")

    useApiStatus(
        activitiesService.signalQuizQuestion,
        "Merci pour votre réponse. Pour ne pas rester bloqué, merci de tout de même choisir une réponse",
        true,
        (data) => {
            setShowModal(false)
            setSelectedType(null)
            setComment("")
            onSignalSubmitted?.(data)
            amplitude.track("quiz.question.signal.submitted", {
                signal_type: selectedType,
                context,
                question_id: questionId,
                comment,
            })
        }
    )

    const handleOpenModal = () => {
        amplitude.track("quiz.question.signal.button_click", {
            context,
            question_id: questionId,
        })
        setShowModal(true)
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setSelectedType(null)
        setComment("")
    }

    const handleSubmit = () => {
        if (!selectedType || !activityId || !questionId) return

        props.apiRequest(
            activitiesService.signalQuizQuestion,
            { activityId },
            {
                quiz_question_id: questionId,
                signal_type: selectedType,
                context,
                comment,
            }
        )
    }

    const renderSignalOption = (type) => {
        const isSelected = selectedType === type
        return (
            <TouchableOpacity
                key={type}
                style={[
                    styles.signalOption,
                    isSelected && styles.signalOptionSelected,
                ]}
                onPress={() => setSelectedType(type)}
                activeOpacity={0.7}
            >
                <FontAwesome5
                    name={QUIZ_SIGNAL_ICONS[type]}
                    size={18}
                    color={isSelected ? theme.colors.primary : theme.colors.grey}
                    solid
                />
                <TextM
                    fontWeight={isSelected ? "SemiBold" : "Regular"}
                    color={isSelected ? theme.colors.primary : theme.colors.dark}
                    style={styles.signalOptionText}
                >
                    {QUIZ_SIGNAL_LABELS[type]}
                </TextM>
                {isSelected && (
                    <FontAwesome5
                        name="check"
                        size={16}
                        color={theme.colors.primary}
                        solid
                    />
                )}
            </TouchableOpacity>
        )
    }

    return (
        <>
            <TouchableOpacity
                onPress={handleOpenModal}
                style={[styles.signalButton, buttonStyle]}
                activeOpacity={0.7}
            >
                <View style={styles.iconContainer}>
                    <FontAwesome5
                        name="flag"
                        size={12}
                        color={theme.colors.light}
                    />
                </View>
                <TextM
                    fontWeight="Medium"
                    fontSize={13}
                    color={theme.colors.light}
                >
                    {label}
                </TextM>
            </TouchableOpacity>

            <ModalM
                visible={showModal}
                onDismiss={handleCloseModal}
                dismissable={true}
                withExitButton={true}
            >
                <View style={styles.modalContent}>
                    <TextM
                        fontWeight="Bold"
                        fontSize={18}
                        style={styles.modalTitle}
                    >
                        Signaler cette question
                    </TextM>

                    <TextM
                        color={theme.colors.grey}
                        fontSize={14}
                        style={styles.modalSubtitle}
                    >
                        Quel est le probleme avec cette question ?
                    </TextM>

                    <View style={styles.optionsContainer}>
                        {Object.values(QUIZ_SIGNAL_TYPES).map(renderSignalOption)}
                    </View>

                    {/* Always show comment input */}
                    <View style={styles.commentContainer}>
                        <UncontrolledTextInputM
                            value={comment}
                            onChangeText={setComment}
                            placeholder="Commentaire (facultatif)"
                            style={styles.commentInput}
                            multiline
                            numberOfLines={3}
                        />
                    </View>

                    <FilledButtonM
                        label="Envoyer"
                        onPress={handleSubmit}
                        disabled={!selectedType}
                        loading={props.signalLoading}
                        style={styles.submitButton}
                    />
                </View>
            </ModalM>
        </>
    )
}

const styles = StyleSheet.create({
    commentContainer: {
        marginTop: 18,
        marginBottom: 4,
    },
    commentInput: {
        minHeight: 48,
        marginBottom: 2,
    },
    signalButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: 10,
        paddingHorizontal: 16,
        gap: 8,
    },
    iconContainer: {
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: theme.colors.lighter,
        alignItems: "center",
        justifyContent: "center",
    },
    modalContent: {
        padding: 20,
    },
    modalTitle: {
        textAlign: "center",
        marginBottom: 8,
    },
    modalSubtitle: {
        textAlign: "center",
        marginBottom: 20,
    },
    optionsContainer: {
        gap: 10,
    },
    signalOption: {
        flexDirection: "row",
        alignItems: "center",
        padding: 14,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
    signalOptionSelected: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    signalOptionText: {
        marginLeft: 12,
        marginRight: 12,
        flex: 1,
    },
    submitButton: {
        marginTop: 20,
    },
})

const mapStateToProps = (state) => ({
    signalLoading: state.api.signalQuizQuestion?.request,
})

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(mapStateToProps, mapDispatchToProps)(QuizQuestionSignalButton)
